import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { romaneioService, Romaneio, ProdutoRomaneio } from '../../services/romaneio';

const RomaneioVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Buscar dados do romaneio
  const { data: romaneio, isLoading, error } = useQuery({
    queryKey: ['romaneio', id],
    queryFn: () => romaneioService.getById(id!),
    enabled: !!id,
  });

  const handleBack = () => {
    navigate('/romaneio');
  };

  const handleEdit = () => {
    navigate(`/romaneio/editar/${id}`);
  };

  const handlePrint = async () => {
    if (id) {
      try {
        await romaneioService.imprimir(id);
      } catch (error) {
        console.error('Erro ao imprimir romaneio:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !romaneio) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography color="error">Erro ao carregar os dados do romaneio.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Romaneio #{romaneio.id}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Editar
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Imprimir
          </Button>
        </Box>
      </Box>

      {/* Informações Principais */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              Informações do Funcionário
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Funcionário</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {romaneio.funcionario || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">ID do Funcionário</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {romaneio.idFuncionario || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              Informações da Praça
            </Typography>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Praça</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {romaneio.praca || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">ID da Praça</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {romaneio.idPraca || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Datas e Status */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="primary" />
              Datas do Romaneio
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Data de Saída</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {romaneio.dataSaidaString || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Data de Retorno</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {romaneio.dataRetornoString || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Status</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Devolução</Typography>
                {romaneio.devolvido ? (
                  <Chip label="Devolvido" color="success" size="small" />
                ) : (
                  <Chip label="Não Devolvido" color="warning" size="small" />
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Finalizado</Typography>
                {romaneio.finalizado ? (
                  <Chip label="Finalizado" color="success" size="small" />
                ) : (
                  <Chip label="Em Andamento" color="info" size="small" />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Produtos */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Produtos do Romaneio ({romaneio.produtos?.length || 0})
          </Typography>
          
          {romaneio.produtos && romaneio.produtos.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Qtd. Disponível</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valor Unitário</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valor Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {romaneio.produtos.map((produto) => {
                    const valorUnitario = produto.precoVenda ? parseFloat(produto.precoVenda) : 0;
                    const valorTotal = valorUnitario * produto.quantidade;
                    
                    return (
                      <TableRow key={produto.id}>
                        <TableCell>{produto.id}</TableCell>
                        <TableCell>{produto.nomeProduto || '-'}</TableCell>
                        <TableCell>{produto.referencia || '-'}</TableCell>
                        <TableCell>{produto.quantidade}</TableCell>
                        <TableCell>{produto.quantidadeDisponivel || '-'}</TableCell>
                        <TableCell>
                          {valorUnitario > 0 
                            ? `R$ ${valorUnitario.toFixed(2)}`
                            : '-'
                          }
                        </TableCell>
                        <TableCell>
                          {valorTotal > 0 
                            ? `R$ ${valorTotal.toFixed(2)}`
                            : '-'
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                Nenhum produto encontrado neste romaneio.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RomaneioVisualizar;
